import React, { useEffect, useRef, useState } from 'react';
import { T, TF } from 'react-translator-component';
import { currencyFormat } from '../../functions';
import { Row, Col } from 'react-bootstrap';
import { useTotalCalculation } from './useTotalCalculation';

const QuestionTotals = ({
    values,
    settings,
    currency = null,
    addon = null,
    onPriceChange = null,
    roomCategory = null,
    useRoomCategory = false,
    checkin = null
}) => {
    // Estado para rastrear cambios en el total (evitar notificar dos veces el mismo valor)
    const prevTotalRef = useRef(null);
    // Ref para onPriceChange y no depender de él en useEffect (evita bucle si el padre no memoiza)
    const onPriceChangeRef = useRef(onPriceChange);
    onPriceChangeRef.current = onPriceChange;

    // Estado consolidado para invalidación
    const [invalidState, setInvalidState] = useState(null);

    // Flags del date-selector: si oculta checkout/hoy, no contar esos días en el total
    const settingsObj = typeof settings === 'string'
        ? (() => { try { return JSON.parse(settings); } catch { return null; } })()
        : settings;
    const dateSelectorQuestion = settingsObj?.questions?.find(q => q.type === 'date-selector');
    const hideCheckoutDate = dateSelectorQuestion?.hideCheckoutDate ?? false;
    const hideToday = dateSelectorQuestion?.hideToday ?? false;

    // Hook personalizado que calcula items y total
    const calculationResult = useTotalCalculation({
        values,
        settings: settingsObj ?? settings,
        useRoomCategory,
        roomCategory,
        addonPrice: addon?.basePrice ?? addon?.price,
        checkoutDate: checkin?.dateEnd ?? dateSelectorQuestion?.endDate ?? null,
        hideCheckoutDate,
        hideToday
    });

    const { items, total, isInvalid, invalidCount, invalidReason } = calculationResult;

    // Actualizar estado de validación cuando cambie el resultado del cálculo
    useEffect(() => {
        if (isInvalid) {
            setInvalidState({
                isInvalid: true,
                invalidCount: invalidCount,
                invalidReason: invalidReason
            });
        } else {
            setInvalidState(null);
        }
    }, [isInvalid, invalidCount, invalidReason]);

    // Notificar cambios de total al componente padre (sin depender de onPriceChange para evitar bucle)
    useEffect(() => {
        const notify = onPriceChangeRef.current;
        if (!notify) return;

        // Si hay validación de fechas inválidas → notificar null solo si antes teníamos total
        if (invalidState?.isInvalid) {
            if (prevTotalRef.current !== null) {
                prevTotalRef.current = null;
                notify(null);
            }
            return;
        }
        // Si hay datos válidos y total > 0
        if (items.length > 0 && total > 0) {
            if (prevTotalRef.current !== total) {
                prevTotalRef.current = total;
                notify(total);
            }
            return;
        }
        // Si no hay datos o total <= 0 → notificar null solo una vez
        if (prevTotalRef.current !== null) {
            prevTotalRef.current = null;
            notify(null);
        }
    }, [total, items.length, invalidState?.isInvalid]);

    // Renderizar desglose de totales
    if (items.length > 0 && total > 0 && !invalidState?.isInvalid) {
        return (
            <div
                className="upsellings-questions-item totals-question mt-3"
                style={{ padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}
            >
                <Row className="d-flex justify-content-between align-items-start">
                    <Col xs={2}>
                        <div style={{ fontWeight: 'bold', fontSize: '1em' }}>
                            {T('general.total')}
                        </div>
                    </Col>
                    <Col xs={10} className="text-right">
                        {items.map((item, idx) => (
                            <div
                                key={`row-item-${idx}`}
                                style={{ fontSize: '0.85em', color: '#666', marginBottom: '6px' }}
                            >
                                {item.quantity}x {item.label}:{' '}
                                {currency
                                    ? currencyFormat(item.subtotal, currency)
                                    : `${item.subtotal}€`}
                            </div>
                        ))}
                        <div
                            style={{
                                fontSize: '1.5em',
                                fontWeight: 'bold',
                                textAlign: 'right',
                                color: '#333',
                                marginBottom: '0'
                            }}
                        >
                            {currency ? currencyFormat(total, currency) : `${total}€`}
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }

    // Renderizar error: pasajeros no seleccionados o tarifas no disponibles
    if (invalidState?.isInvalid) {
        return (
            <div
                className="upsellings-questions-item totals-question mt-3"
                style={{ padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}
            >
                {invalidState.invalidReason === 'select-passengers' ? (
                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '6px' }}>
                        {T('general.must-select-passengers')}
                    </div>
                ) : invalidState.invalidCount === 0 ? (
                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '6px' }}>
                        {T('general.no-rates-available-for-selected-dates')}
                    </div>
                ) : (
                    <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '6px' }}>
                        {TF('general.invalid-dates', [invalidState.invalidCount])}
                    </div>
                )}
            </div>
        );
    }

    // No mostrar nada si no hay datos válidos
    return null;
};

export default QuestionTotals;
